import { useCallback, useMemo } from "react";

import { useSearchParams } from "react-router-dom";

import { DateRange } from "src/base-components/DateRangePicker";
import { EventFilterFormData } from "src/eventsCatalogue/filters/types";
import {
  parseParams,
  stringifyParams,
} from "src/eventsCatalogue/filters/utils";

export type UrlFilterParams = {
  formData: EventFilterFormData;
  dateRange: {
    from: Date;
    to: Date;
  };
};

export const getFiltersUrlParamKey = (eventType: string) => ({
  filters: `events-filters-${eventType}`,
  from: `events-from-${eventType}`,
  to: `events-to-${eventType}`,
});

export const useEventFiltersUrl = (eventType: string) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlParamKeys = useMemo(
    () => getFiltersUrlParamKey(eventType),
    [eventType],
  );

  // Derive filter params from URL
  const urlFilterParams = useMemo<UrlFilterParams | null>(() => {
    const filtersParam = searchParams.get(urlParamKeys.filters);
    const fromParam = searchParams.get(urlParamKeys.from);
    const toParam = searchParams.get(urlParamKeys.to);

    if (!filtersParam || !fromParam || !toParam) {
      return null;
    }

    const parsedFilters = parseParams(filtersParam);
    if (!parsedFilters) {
      return null;
    }

    const fromDate = new Date(fromParam);
    const toDate = new Date(toParam);

    // Validate dates
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      return null;
    }

    return {
      formData: parsedFilters,
      dateRange: { from: fromDate, to: toDate },
    };
  }, [searchParams, urlParamKeys]);

  // Update URL with new filters (formData optional - if undefined, only updates dates)
  const setFilters = useCallback(
    (
      formData: EventFilterFormData | undefined,
      dateRange: DateRange | undefined,
      clearParamKeys?: string[],
    ) => {
      if (!dateRange?.from || !dateRange?.to) {
        return;
      }

      const fromTimestamp = dateRange.from.toISOString();
      const toTimestamp = dateRange.to.toISOString();

      setSearchParams((searchParams) => {
        // Clear specified params atomically
        clearParamKeys?.forEach((key) => searchParams.delete(key));

        if (formData) {
          searchParams.set(urlParamKeys.filters, stringifyParams(formData));
        }
        searchParams.set(urlParamKeys.from, fromTimestamp);
        searchParams.set(urlParamKeys.to, toTimestamp);
        return searchParams;
      });
    },
    [setSearchParams, urlParamKeys],
  );

  // Clear filters from URL
  const clearFilters = useCallback(() => {
    setSearchParams((searchParams) => {
      searchParams.delete(urlParamKeys.filters);
      searchParams.delete(urlParamKeys.from);
      searchParams.delete(urlParamKeys.to);
      return searchParams;
    });
  }, [setSearchParams, urlParamKeys]);

  return {
    urlFilterParams,
    hasFilters: urlFilterParams !== null,
    setFilters,
    clearFilters,
  };
};
